// src/services/conversationService.ts
import { apiService } from './api';
import { socketService } from './socketService';
import type {
  ConversationDetails,
  ConversationDetail,
  Reply,
  ConversationUpdate,
  ReplyCreate,
  ReplyUpdate,
  PhaseCreate,
  PhaseUpdate,
  ConversationPhase,
  PaginationParams,
  ReplyFilter,
  PaginatedRepliesResponse,
  Reaction,
  ReactionCreate,
  TagItem,
  Attachment,
  EditHistoryItem,
  CopyMoveProjectRequest,
} from '../types/conversation';

class ConversationService
{
  /**
   * Obtiene los detalles completos de una conversación
   */
  async getConversationDetails(
    conversationId: number,
    options: {
      layersToHide?: string;
      getAllReplies?: boolean;
      isConversationDetailsView?: boolean;
      totalReplies?: number;
      isEnabled?: boolean;
      isPersonConversation?: boolean;
      homeProfilePersonId?: number;
    } = {},
  ): Promise<ConversationDetails>
  {
    try
    {
      const {
        layersToHide = '',
        getAllReplies = false,
        isConversationDetailsView = true,
        totalReplies = 0,
        isEnabled = true,
        isPersonConversation = false,
        homeProfilePersonId,
      } = options;

      const params = new URLSearchParams({
        conversationId: conversationId.toString(),
        layersToHide,
        getAllReplies: getAllReplies.toString(),
        isConversationDetailsView: isConversationDetailsView.toString(),
        totalReplies: totalReplies.toString(),
        isEnabled: isEnabled.toString(),
      });

      // Agregar parámetros opcionales para perfil de usuario
      if (isPersonConversation)
      {
        params.append('isPersonConversation', 'true');
      }
      if (homeProfilePersonId !== undefined)
      {
        params.append('homeProfilePersonId', homeProfilePersonId.toString());
      }

      const response = await apiService.post<ConversationDetails>(
        `/Conversation/GetConversationById?${params.toString()}`,
        {}, // Body vacío ya que los parámetros van en query string
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting conversation details:', error);
      throw error;
    }
  }

  /**
   * Actualiza una conversación
   */
  async updateConversation(data: ConversationUpdate): Promise<ConversationDetails>
  {
    try
    {
      const response = await apiService.put<ConversationDetails>(
        '/conversation/updateconversation',
        { ...data, SocketConnectionId: socketService.connectionId },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error updating conversation:', error);
      throw error;
    }
  }

  /**
   * Elimina una conversación (mueve a papelera)
   */
  async deleteConversation(
    DashboardGroupIdDefault: number,
    dashboardGroupConvs: any,
    SocketConnectionId: string,
  ): Promise<void>
  {
    // Fall back to the live connection so the backend echo is recognized as our own action
    const request = {
      DashboardGroupIdDefault,
      dashboardGroupConvs,
      SocketConnectionId: SocketConnectionId || socketService.connectionId,
    };
    try
    {
      await apiService.post(`/conversation/DisableConversations`, request);
    }
    catch (error)
    {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }

  /**
   * Elimina permanentemente una conversación desde la papelera de reciclaje
   */
  async deleteConversationPermanently(
    conversationId: number,
    dashboardGroupId: number,
  ): Promise<void>
  {
    try
    {
      const body = `ConversationIds=${conversationId}&Delete=true&SocketConnectionId=${socketService.connectionId}`;
      await apiService.post(
        `Conversation/RestoreConversations?ConversationIds=${conversationId}&Delete=true&SocketConnectionId=${socketService.connectionId}`,
        body,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      );
      await apiService.post('conversation/UpdatePercentDashboardGroupConversations', {
        dashboardGroupConvs: [{ DashboardGroupId: dashboardGroupId, ConversationId: 0 }],
        DashboardGroupIdDefault: 0,
      });
    }
    catch (error)
    {
      console.error('Error deleting conversation permanently:', error);
      throw error;
    }
  }

  /**
   * Restaura una conversación de la papelera
   */
  async restoreConversation(conversationId: number, socketConnectionId?: string): Promise<void>
  {
    try
    {
      await apiService.put(`/conversation/restore`, { conversationId, socketConnectionId });
    }
    catch (error)
    {
      console.error('Error restoring conversation:', error);
      throw error;
    }
  }

  /**
   * Restaura una conversación desde la papelera de reciclaje (endpoint correcto)
   */
  async restoreConversationFromBin(conversationId: number): Promise<void>
  {
    try
    {
      const body = `ConversationIds=${conversationId}&Delete=false&SocketConnectionId=${socketService.connectionId}`;
      await apiService.post(
        `Conversation/RestoreConversations?ConversationIds=${conversationId}&Delete=false&SocketConnectionId=${socketService.connectionId}`,
        body,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      );
    }
    catch (error)
    {
      console.error('Error restoring conversation from bin:', error);
      throw error;
    }
  }

  /**
   * Agrega una respuesta (reply) a una conversación
   */
  async addReply(data: ReplyCreate): Promise<Reply>
  {
    try
    {
      const formData = new FormData();
      formData.append('ConversationId', data.ConversationId.toString());
      formData.append('Body', data.Body);
      formData.append('LayerIds', JSON.stringify(data.LayerIds));

      if (data.Files && data.Files.length > 0)
      {
        data.Files.forEach(file =>
        {
          formData.append('files', file);
        });
      }

      if (data.SocketConnectionId)
      {
        formData.append('SocketConnectionId', data.SocketConnectionId);
      }

      const response = await apiService.post<Reply>('/conversation/addreply', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    }
    catch (error)
    {
      console.error('Error adding reply:', error);
      throw error;
    }
  }

  /**
   * Actualiza una respuesta existente
   */
  async updateReply(data: ReplyUpdate): Promise<Reply>
  {
    try
    {
      const response = await apiService.put<Reply>('/conversation/updatereply', data);
      return response;
    }
    catch (error)
    {
      console.error('Error updating reply:', error);
      throw error;
    }
  }

  /**
   * Elimina una respuesta
   */
  async deleteReply(replyId: number, socketConnectionId?: string): Promise<void>
  {
    try
    {
      await apiService.delete(
        `/conversation/deletereply?replyId=${replyId}&socketConnectionId=${socketConnectionId || ''}`,
      );
    }
    catch (error)
    {
      console.error('Error deleting reply:', error);
      throw error;
    }
  }

  /**
   * Marca una conversación como favorita
   */
  async toggleFavorite(conversationId: number, isFavorite: boolean): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/togglefavorite', {
        conversationId,
        isFavorite,
      });
    }
    catch (error)
    {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  }

  /**
   * Marca una conversación con bandera roja
   */
  async toggleRedFlag(conversationId: number, isRedFlag: boolean): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/toggleredflag', {
        conversationId,
        isRedFlag,
      });
    }
    catch (error)
    {
      console.error('Error toggling red flag:', error);
      throw error;
    }
  }

  /**
   * Obtiene el historial de ediciones de una conversación
   */
  async getEditHistory(conversationId: number): Promise<EditHistoryItem[]>
  {
    try
    {
      const response = await apiService.get<EditHistoryItem[]>(
        `/conversation/getedithistory?conversationId=${conversationId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting edit history:', error);
      throw error;
    }
  }

  // ==================== Phase Management ====================

  /**
   * Crea una nueva fase/tab en una conversación
   */
  async createPhase(conversationId: number, phase: PhaseCreate): Promise<ConversationPhase>
  {
    try
    {
      const response = await apiService.post<ConversationPhase>(`/conversation/createphase`, {
        ConversationId: conversationId,
        Title: phase.Title,
        Description: phase.Description,
        Order: phase.Order,
        SocketConnectionId: socketService.connectionId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error creating phase:', error);
      throw error;
    }
  }

  /**
   * Actualiza una fase existente
   */
  async updatePhase(
    conversationId: number,
    phaseId: number,
    updates: PhaseUpdate,
  ): Promise<ConversationPhase>
  {
    try
    {
      const response = await apiService.put<ConversationPhase>(`/conversation/updatephase`, {
        ConversationPhaseId: phaseId,
        ConversationId: conversationId,
        ...updates,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error updating phase:', error);
      throw error;
    }
  }

  /**
   * Elimina una fase
   */
  async deletePhase(conversationId: number, phaseId: number): Promise<void>
  {
    try
    {
      await apiService.delete(
        `/conversation/deletephase?conversationPhaseId=${phaseId}&conversationId=${conversationId}&SocketConnectionId=${socketService.connectionId}`,
      );
    }
    catch (error)
    {
      console.error('Error deleting phase:', error);
      throw error;
    }
  }

  // ==================== Layers, Owners, Security Groups ====================

  /**
   * Actualiza las capas (layers) asignadas a una conversación
   */
  async updateLayers(conversationId: number, layerIds: number[]): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/updatelayers', {
        conversationId,
        layerIds,
        SocketConnectionId: socketService.connectionId,
      });
    }
    catch (error)
    {
      console.error('Error updating layers:', error);
      throw error;
    }
  }

  /**
   * Actualiza los owners/propietarios de una conversación
   */
  async updateOwners(conversationId: number, ownerIds: number[]): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/updateowners', {
        conversationId,
        ownerIds,
      });
    }
    catch (error)
    {
      console.error('Error updating owners:', error);
      throw error;
    }
  }

  async getPersonEnabledStatus(personId: number): Promise<boolean>
  {
    const result = await apiService.get<boolean>(`Person/EnablePerson?personId=${personId}`);
    return result;
  }

  /**
   * Actualiza los security groups de una conversación
   */
  async updateSecurityGroups(conversationId: number, groupIds: number[]): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/updatesecuritygroups', {
        conversationId,
        securityGroupIds: groupIds,
      });
    }
    catch (error)
    {
      console.error('Error updating security groups:', error);
      throw error;
    }
  }

  // ==================== Project Operations ====================

  /**
   * Copia una conversación a otro proyecto
   */
  async copyToProject(request: CopyMoveProjectRequest): Promise<ConversationDetail>
  {
    try
    {
      const response = await apiService.post<ConversationDetail>('/conversation/copytoproject', {
        ConversationId: request.conversationId,
        ProjectId: request.targetProjectId,
        CopyAttachments: request.copyAttachments ?? true,
        CopyReplies: request.copyReplies ?? false,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error copying to project:', error);
      throw error;
    }
  }

  /**
   * Mueve una conversación a otro proyecto
   */
  async moveToProject(request: CopyMoveProjectRequest): Promise<ConversationDetail>
  {
    try
    {
      const response = await apiService.post<ConversationDetail>('/conversation/movetoproject', {
        ConversationId: request.conversationId,
        ProjectId: request.targetProjectId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error moving to project:', error);
      throw error;
    }
  }

  // ==================== Reply Pagination & Filtering ====================

  /**
   * Obtiene más respuestas con paginación
   */
  async getMoreReplies(
    conversationId: number,
    params: PaginationParams,
    filter?: ReplyFilter,
  ): Promise<PaginatedRepliesResponse>
  {
    try
    {
      const response = await apiService.post<GetMoreRepliesResponse>(
        '/conversation/getmorereplies',
        {
          ConversationId: conversationId,
          PageNumber: params.pageNumber,
          PageSize: params.pageSize,
          PhaseId: filter?.phaseId,
          ReplyTab: filter?.replyTab,
          SearchText: filter?.searchText,
          LayerIds: filter?.layerIds,
          StartDate: filter?.startDate,
          EndDate: filter?.endDate,
          IncludeDetailUpdates: filter?.includeDetailUpdates ?? true,
        },
      );

      return {
        replies: response.Replies,
        hasMore: response.HasMore,
        total: response.Total,
        pageNumber: response.PageNumber,
        pageSize: response.PageSize,
      };
    }
    catch (error)
    {
      console.error('Error getting more replies:', error);
      throw error;
    }
  }

  /**
   * Obtiene IDs de respuestas filtradas (para búsqueda/navegación)
   */
  async getReplyIdsFromConversationWithFilter(
    conversationId: number,
    filter: ReplyFilter,
  ): Promise<number[]>
  {
    try
    {
      const response = await apiService.post<number[]>('/conversation/getreplyidswithfilter', {
        ConversationId: conversationId,
        PhaseId: filter.phaseId,
        ReplyTab: filter.replyTab,
        SearchText: filter.searchText,
        LayerIds: filter.layerIds,
        StartDate: filter.startDate,
        EndDate: filter.endDate,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error getting reply IDs with filter:', error);
      return [];
    }
  }

  // ==================== Reactions ====================

  /**
   * Agrega una reacción a una conversación o respuesta
   */
  async addReaction(data: ReactionCreate & { conversationId: number }): Promise<Reaction>
  {
    try
    {
      const response = await apiService.post<Reaction>('/conversation/addreaction', {
        ConversationId: data.conversationId,
        ReplyId: data.replyId,
        Emoji: data.emoji,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error adding reaction:', error);
      throw error;
    }
  }

  /**
   * Elimina una reacción
   */
  async removeReaction(
    conversationId: number,
    reactionId: number,
    replyId?: number | null,
  ): Promise<void>
  {
    try
    {
      await apiService.delete(
        `/conversation/removereaction?conversationId=${conversationId}&reactionId=${reactionId}${
          replyId ? `&replyId=${replyId}` : ''
        }`,
      );
    }
    catch (error)
    {
      console.error('Error removing reaction:', error);
      throw error;
    }
  }

  // ==================== Tags & Mentions ====================

  /**
   * Obtiene la lista de personas y grupos para menciones
   */
  async getTagList(projectId: number): Promise<TagItem[]>
  {
    try
    {
      const response = await apiService.get<{ People: any[]; Groups: any[] }>(
        `/conversation/gettaglist?projectId=${projectId}`,
      );

      // Transformar respuesta a TagItem[]
      const people: TagItem[] = (response.People || []).map((p: any) => ({
        id: p.PersonId,
        type: 'person' as const,
        name: p.Name || p.PersonName,
        email: p.Email || p.EmailAddress,
        imageName: p.ImageName,
        displayName: p.Name || p.PersonName,
      }));

      const groups: TagItem[] = (response.Groups || []).map((g: any) => ({
        id: g.GroupId,
        type: 'group' as const,
        name: g.Name || g.GroupName,
        imageName: g.ImageName,
        displayName: g.Name || g.GroupName,
      }));

      return [...people, ...groups];
    }
    catch (error)
    {
      console.error('Error getting tag list:', error);
      return [];
    }
  }

  // ==================== File Operations ====================

  /**
   * Sube un archivo adjunto a una conversación o respuesta
   */
  async uploadAttachment(
    file: File,
    conversationId: number,
    replyId?: number,
    onProgress?: (progress: number) => void,
  ): Promise<Attachment>
  {
    try
    {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ConversationId', conversationId.toString());
      if (replyId)
      {
        formData.append('ReplyId', replyId.toString());
      }

      const response = await apiService.post<Attachment>(
        '/conversation/uploadattachment',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: progressEvent =>
          {
            if (onProgress && progressEvent.total)
            {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onProgress(percentCompleted);
            }
          },
        },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  }

  /**
   * Elimina un archivo adjunto
   */
  async deleteAttachment(attachmentId: number): Promise<void>
  {
    try
    {
      await apiService.delete(`/conversation/deleteattachment?attachmentId=${attachmentId}`);
    }
    catch (error)
    {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  }

  // ==================== Follow/Subscribe ====================

  /**
   * Sigue o deja de seguir una conversación
   */
  async toggleFollow(conversationId: number, isFollowing: boolean): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/togglefollow', {
        conversationId,
        isFollowing,
      });
    }
    catch (error)
    {
      console.error('Error toggling follow:', error);
      throw error;
    }
  }

  /**
   * Archiva o desarchivauna conversación
   */
  async toggleArchive(conversationId: number, isArchived: boolean): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/togglearchive', {
        conversationId,
        isArchived,
      });
    }
    catch (error)
    {
      console.error('Error toggling archive:', error);
      throw error;
    }
  }

  /**
   * Marca una conversación como importante
   */
  async toggleImportant(conversationId: number, isImportant: boolean): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/toggleimportant', {
        conversationId,
        isImportant,
      });
    }
    catch (error)
    {
      console.error('Error toggling important:', error);
      throw error;
    }
  }

  /**
   * Marca una conversación como completa
   */
  async toggleComplete(
    conversationId: number,
    finished: boolean,
    dashboardGroupId?: number,
  ): Promise<void>
  {
    try
    {
      await apiService.put('/conversation/togglecomplete', {
        conversationId,
        finished,
        dashboardGroupId,
      });
    }
    catch (error)
    {
      console.error('Error toggling complete:', error);
      throw error;
    }
  }

  /**
   * Marca conversaciones archivada
   */
  async ArchivedConversations(request: any): Promise<void>
  {
    try
    {
      await apiService.post('/conversation/ArchivedConversations', {
        conversations: request,
      });
    }
    catch (error)
    {
      console.error('Error marking as viewed:', error);
      throw error;
    }
  }

  /**
   * Remueve la conversacion de la columna
   */
  async DeleteDashboardGroupConversations(request: any): Promise<void>
  {
    try
    {
      await apiService.post('/conversation/DeleteDashboardGroupConversations', {
        ...request,
        SocketConnectionId: socketService.connectionId,
      });
    }
    catch (error)
    {
      console.error('Error marking as viewed:', error);
      throw error;
    }
  }

  // /**
  //  * Add dashboard group by parameters
  //  */
  // async Adddashboardgroupbyparameters(
  //   dashboardId: number,
  //   dashboardGroupId: number,
  //   conversationId: number,
  //   dashboardGroupIdSend: number,
  //   includeAllConversations: boolean,
  //   ParentConversationId: number,
  //   socketid: string = "",
  //   isBookmark: boolean = false): Promise<void> {
  //   try {
  //     await apiService.get('/conversation/adddashboardgroupbyparameters');
  //   } catch (error) {
  //     console.error('Error marking as viewed:', error);
  //     throw error;
  //   }
  // }

  /**
   * Add dashboard group by parameters
   */
  async Adddashboardgroupbyparameters(
    dashboardId: number,
    dashboardGroupId: number,
    conversationId: number,
    dashboardGroupIdSend: number,
    includeAllConversations: boolean,
    ParentConversationId: number,
    socketid: string = '',
    isBookmark: boolean = false,
  )
  {
    try
    {
      const response = await apiService.get(
        `/dashboard/adddashboardgroupbyparameters?dashboardId=${dashboardId}&dashboardGroupId=${dashboardGroupId}&conversationId=${conversationId}&dashboardGroupIdSend=${dashboardGroupIdSend}&includeAllConversations=${includeAllConversations}&ParentConversationId=${ParentConversationId}&socketid=${socketid}&isBookmark=${isBookmark}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error marking as viewed:', error);
      throw error;
    }
  }
}

// Helper type for GetMoreReplies response from API
interface GetMoreRepliesResponse {
  Replies: Reply[];
  HasMore: boolean;
  Total: number;
  PageNumber: number;
  PageSize: number;
}

export const conversationService = new ConversationService();
